import { Menu } from 'lucide-react';
import * as React from 'react';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import { AppSidebar } from '@/components/app-sidebar';
import { AssistantButton } from '@/components/assistant/assistant-button';
import { AssistantProvider } from '@/components/assistant/assistant-context';
import { AssistantPanel } from '@/components/assistant/assistant-panel';
import { DashboardHeaderTarget } from '@/components/dashboard-header-actions';
import { Logo } from '@/components/logo';
import { ProjectSwitcher } from '@/components/project-switcher';
import { UserMenu } from '@/components/user-menu';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

const SIDEBAR_STORAGE_KEY = 'gainsignal:sidebar-expanded';

export const DashboardLayout = () => {
  const projectMatch = useMatch('/projects/:projectId/*');
  const matchedProjectId = projectMatch?.params.projectId;
  const currentProjectId =
    matchedProjectId && matchedProjectId !== 'new'
      ? matchedProjectId
      : undefined;
  const isSetup = Boolean(useMatch('/projects/:projectId/setup'));
  const hasProjectWorkspace = Boolean(currentProjectId) && !isSetup;
  const isOverview = Boolean(useMatch('/projects/:projectId'));
  const [headerTarget, setHeaderTarget] = React.useState<HTMLDivElement | null>(
    null,
  );
  const location = useLocation();
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [sidebarExpanded, setSidebarExpanded] = React.useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarExpanded));
    } catch {
      // Storage is optional; the in-memory preference still works.
    }
  }, [sidebarExpanded]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <DashboardHeaderTarget.Provider value={headerTarget}>
      <AssistantProvider projectId={currentProjectId}>
        <div className="flex min-h-svh bg-canvas">
          {hasProjectWorkspace ? (
            <div className="sticky top-0 hidden h-svh shrink-0 md:block">
              <AppSidebar
                currentProjectId={currentProjectId}
                collapsed={!sidebarExpanded}
                onToggle={() => setSidebarExpanded((current) => !current)}
              />
            </div>
          ) : null}

          {hasProjectWorkspace ? (
            <Dialog
              open={mobileSidebarOpen}
              onOpenChange={setMobileSidebarOpen}
            >
              <DialogContent className="left-0 top-0 h-svh w-[18rem] max-w-[calc(100vw-2rem)] translate-x-0 translate-y-0 rounded-none border-y-0 border-l-0 p-0">
                <DialogTitle className="sr-only">
                  Project navigation
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Navigate this project&apos;s analytics, data sources, and
                  details.
                </DialogDescription>
                <AppSidebar
                  currentProjectId={currentProjectId}
                  mobile
                  onNavigate={() => setMobileSidebarOpen(false)}
                />
              </DialogContent>
            </Dialog>
          ) : null}

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="sticky top-0 z-40 shrink-0 border-b border-border bg-canvas">
              <div className="flex min-h-14 flex-wrap items-center gap-x-2 px-2 sm:px-4 md:px-6">
                <div className="flex h-14 min-w-0 flex-1 items-center gap-2">
                  {hasProjectWorkspace ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-10 shrink-0 md:hidden"
                        onClick={() => setMobileSidebarOpen(true)}
                        aria-label="Open project navigation"
                      >
                        <Menu />
                      </Button>

                      <ProjectSwitcher currentProjectId={currentProjectId} />
                      {isOverview ? (
                        <span className="ml-auto shrink-0 pr-2 text-xs text-ink-muted md:hidden">
                          Opportunities
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <Logo showWordmark />
                  )}

                  {!hasProjectWorkspace ? (
                    <div className="ml-auto">
                      <UserMenu />
                    </div>
                  ) : null}
                </div>
                {hasProjectWorkspace ? <AssistantButton /> : null}
                <div
                  ref={setHeaderTarget}
                  className="flex min-w-0 basis-full items-center justify-between gap-2 border-t border-border px-2 py-2 empty:hidden lg:ml-auto lg:basis-auto lg:border-0 lg:px-0 lg:py-0"
                />
              </div>
            </header>

            <main className="mx-auto w-full max-w-[96rem] flex-1 px-4 py-6 sm:px-6 lg:px-8">
              <Outlet />
            </main>
          </div>
        </div>
        <AssistantPanel />
      </AssistantProvider>
    </DashboardHeaderTarget.Provider>
  );
};
